import React, { Fragment, useReducer } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import Select from "react-select";

import { LoadingButton } from "../shared/loading-button";
import TextInput from "../text-input";
import { types, crated, weights } from "../../helpers/pets";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }
    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }
    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        _id: undefined,
        name: "",
        type: "",
        breed: "",
        weight: null,
        isCrated: null,
      };
    }
    default:
      return state;
  }
};

const REQUIRED_FIELDS_ERROR = "Please fill in all required fields and retry.";

const isValidWeight = (weight) => weight >= 5 && weight <= 150 && weight % 5 === 0;

const buildPet = (state) => {
  const name = (state.name || "").trim();
  const breed = (state.breed || "").trim();

  if (!name || !state.type) {
    return { error: REQUIRED_FIELDS_ERROR };
  }

  if (state.weight && !isValidWeight(state.weight)) {
    return { error: "Please fill valid weight and retry." };
  }

  const pet = { _id: state._id, name, type: state.type };

  if (state.type === "Dog") {
    if (!breed || !state.weight || state.isCrated === null) {
      return { error: REQUIRED_FIELDS_ERROR };
    }

    return { pet: { ...pet, breed, weight: state.weight, isCrated: state.isCrated } };
  }

  if (state.type === "Cat") {
    return { pet: { ...pet, weight: state.weight || null, isCrated: true } };
  }

  if (state.type === "Other") {
    if (!breed || !state.weight) {
      return { error: REQUIRED_FIELDS_ERROR };
    }

    return { pet: { ...pet, breed, weight: state.weight } };
  }

  return { pet };
};

const SingleProspectPetsTable = (props) => {
  const { pets = [], handleChange, handleToggleEdit, disabled = false } = props;

  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    _id: undefined,
    name: "",
    type: "",
    breed: "",
    weight: null,
    isCrated: null,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleAddPet = (e) => {
    e.preventDefault();

    handleToggleEdit("pets");
    dispatch({
      type: "openModal",
    });
  };

  const handleEditPet = (e, index) => {
    e.preventDefault();

    const pet = pets[index];
    if (!pet) {
      return;
    }

    handleToggleEdit("pets");
    dispatch({
      type: "openModal",
      payload: {
        ...pet,
      },
    });
  };

  const handleDeletePet = (e, index) => {
    e.preventDefault();

    const pet = pets[index];
    if (!pet) {
      return;
    }

    handleToggleEdit("pets");
    dispatch({
      type: "openDeleteModal",
      payload: pet,
    });
  };

  const handleCloseModal = () => {
    handleToggleEdit();
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleTypeChange = (type) => {
    dispatch({
      type: "fieldChanged",
      field: "type",
      value: type.value,
    });
  };

  const handleIsCratedChange = (isCrated) => {
    dispatch({
      type: "fieldChanged",
      field: "isCrated",
      value: isCrated.value === "true",
    });
  };

  const handleWeightChange = (weight) => {
    dispatch({
      type: "fieldChanged",
      field: "weight",
      value: weight ? weight.value : null,
    });
  };

  const handleSubmitPet = async (e) => {
    e.preventDefault();

    const { pet, error } = buildPet(state);

    if (error) {
      return toast.error(error, toastOptions);
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange(
      state._id ?
        (pets || []).map((item) => item._id === state._id ? pet : item) :
        [...(pets || []), pet],
    );
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  const isFormValid = !buildPet(state).error;

  const handleDeletePetConfirm = async (e) => {
    e.preventDefault();

    if (!state._id) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange((pets || []).filter((item) => item._id !== state._id));
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  return (
    <Fragment>
      <h4 className="mt-20" data-testid="saved-pets-heading">Saved Pets</h4>

      <div className="prospect-table prospect-pets-table" data-testid="saved-pets-table">
        <table>
          <thead>
          <tr>
            <th>Pet Name</th>
            <th>Type</th>
            <th>Breed / Other</th>
            <th>Weight</th>
            <th>Crated</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          {!pets?.length && <tr data-testid="saved-pets-empty">
            <td colSpan={5}>No pets are saved yet</td>
            <td/>
          </tr>}
          {pets?.map((({ _id, name, type, breed, weight, isCrated }, index) => (
            <tr key={_id} data-testid="saved-pet-row">
              <td>{name}</td>
              <td>{type}</td>
              <td>{breed}</td>
              <td>{weight ? `${weight}lb` : ""}</td>
              <td>{type === "Other" ? "" : isCrated ? "Yes" : "No"}</td>
              <td>
                <button
                  className="base-button icon-button evo-blue"
                  data-testid="edit-saved-pet-button"
                  onClick={(e) => {
                    handleEditPet(e, index);
                  }}
                  disabled={disabled}
                >
                  <span className="material-icons">edit</span>
                </button>

                <button
                  className="base-button icon-button btn-danger ml-2"
                  data-testid="delete-saved-pet-button"
                  onClick={(e) => {
                    handleDeletePet(e, index);
                  }}
                  disabled={disabled}
                >
                  <span className="material-icons">delete</span>
                </button>
              </td>
            </tr>
          )))}
          </tbody>
        </table>
      </div>

      <button
        className="mt-20 action-button base-button base-blue-button"
        data-testid="add-saved-pet-button"
        onClick={handleAddPet}
        disabled={disabled}
      >
        Add Pet
      </button>

      <ReactModal
        isOpen={state.showModal}
        testId="saved-pet-modal"
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            overflowY: "scroll",
            maxWidth: "600px",
            maxHeight: "80vh",
            minWidth: "calc(min(500px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4 data-testid="saved-pet-modal-title">{state._id ? "Edit" : "Add"} Pet</h4>
        </div>

        <div className="modal-body base-modal">
          <form onSubmit={handleSubmitPet} className="w-100 base-form mt-20">
            <div className="w-100 d-flex gap-20">
              <TextInput
                name="name"
                label="Pet Name"
                testId="saved-pet-name"
                required
                handleChange={handleInputChange}
                value={state.name}
                containerClasses="flex-half"
              />

              <div className="flex-half form-group" data-testid="saved-pet-type">
                <label htmlFor="type">
                  Type <span className="required">*</span>
                </label>

                <Select
                  className="type-select"
                  classNamePrefix="select"
                  options={types}
                  placeholder="Select type"
                  onChange={handleTypeChange}
                  value={state.type ? types.find(({ value }) => value === state.type) : null}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {state.type === "Dog" && (
              <>
                <div className="text-muted small mb-5" data-testid="saved-pet-breed-hint">
                  <span>Please specify the breed of your dog</span>
                </div>

                <div className="w-100 d-flex gap-20">
                  <TextInput
                    name="breed"
                    label="Breed"
                    testId="saved-pet-breed"
                    required
                    handleChange={handleInputChange}
                    value={state.breed}
                    containerClasses="flex-half"
                    maxlength="50"
                  />

                  <div className="flex-half form-group" data-testid="saved-pet-weight">
                    <label htmlFor="weight">
                      Weight (lbs) <span className="required">*</span>
                    </label>

                    <Select
                      className="type-select"
                      classNamePrefix="select"
                      options={weights}
                      placeholder="Select weight"
                      onChange={handleWeightChange}
                      value={state.weight ? weights.find(({ value }) => value === state.weight) : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                <div className="w-100 d-flex gap-20">
                  <div className="flex-one form-group" data-testid="saved-pet-cabin">
                    <label htmlFor="isCrated">
                      Will your dog be crated or free in the cabin? <span className="required">*</span>
                    </label>

                    <Select
                      className="type-select"
                      classNamePrefix="select"
                      options={crated}
                      placeholder="Select"
                      onChange={handleIsCratedChange}
                      value={state.isCrated === null ? null : state.isCrated ? crated[0] : crated[1]}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
              </>
            )}

            {state.type === "Cat" && (
              <>
                <div className="cat-notice" data-testid="saved-pet-cat-notice">
                  Please note that all cats must be crated for the duration of the flight unless otherwise approved by
                  the crew of the aircraft.
                </div>

                <div className="w-100 d-flex gap-20">
                  <div className="flex-half form-group" data-testid="saved-pet-weight">
                    <label htmlFor="weight">Weight (lbs)</label>

                    <Select
                      className="type-select"
                      classNamePrefix="select"
                      options={weights}
                      placeholder="Select weight"
                      onChange={handleWeightChange}
                      isClearable
                      value={state.weight ? weights.find(({ value }) => value === state.weight) : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
              </>
            )}

            {state.type === "Other" && (
              <>
                <div className="text-muted small mb-5" data-testid="saved-pet-other-type-hint">
                  <span>Please specify the type of pet you would like to add.</span>
                </div>

                <div className="w-100 d-flex gap-20">
                  <TextInput
                    name="breed"
                    label="Pet Type"
                    testId="saved-pet-breed"
                    required
                    handleChange={handleInputChange}
                    value={state.breed}
                    containerClasses="flex-half"
                    maxlength="50"
                  />

                  <div className="flex-half form-group" data-testid="saved-pet-weight">
                    <label htmlFor="weight">
                      Weight (lbs) <span className="required">*</span>
                    </label>

                    <Select
                      className="type-select"
                      classNamePrefix="select"
                      options={weights}
                      placeholder="Select weight"
                      onChange={handleWeightChange}
                      value={state.weight ? weights.find(({ value }) => value === state.weight) : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
              </>
            )}
          </form>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <LoadingButton
              className="base-button evo-blue flex-half" type="button" onClick={handleSubmitPet}
              data-testid="saved-pet-submit-button"
              isLoading={state.saving}
              disabled={!isFormValid}
            >
              Submit
            </LoadingButton>

            <button
              className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
              data-testid="saved-pet-cancel-button"
              disabled={state.saving}
            >
              Cancel
            </button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        testId="delete-saved-pet-modal"
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4 data-testid="delete-saved-pet-modal-title">Delete Pet</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20 font-weight-bold text-center" data-testid="delete-saved-pet-name">
            {state.name} ({state.type})
          </div>

          <div className="w-100 mt-20 text-center" data-testid="delete-saved-pet-copy">
            Are you sure you want to remove this pet from the prospect's profile?<br/>
            This action is permanent and cannot be undone. Pets already on an itinerary are not affected.
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <LoadingButton
              className="base-button btn-danger flex-half" type="button" onClick={handleDeletePetConfirm}
              data-testid="delete-saved-pet-confirm-button"
              isLoading={state.saving}
            >
              Delete Pet
            </LoadingButton>

            <button
              className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
              data-testid="delete-saved-pet-cancel-button"
              disabled={state.saving}
            >
              Cancel
            </button>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default SingleProspectPetsTable;
